import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useContextSources } from '@/hooks'
import { ContextSourceKinds } from '@/lib/contextSources'
import type { ConnectorType } from '@/types'
import { AlertTriangle, X } from 'lucide-react'
import { AI_PROVIDER_DISCONNECT_EFFECTS, CONNECTOR_LABELS } from './connectorConstants'

interface DisconnectConfirmDialogProps {
  projectId: string
  connectorType: ConnectorType
  connectorId?: string
  connectionName?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void | Promise<void>
  isDisconnecting: boolean
}

export function DisconnectConfirmDialog({
  projectId,
  connectorType,
  connectorId,
  connectionName,
  open,
  onOpenChange,
  onConfirm,
  isDisconnecting,
}: Readonly<DisconnectConfirmDialogProps>) {
  const label = CONNECTOR_LABELS[connectorType]
  const aiProviderEffect = AI_PROVIDER_DISCONNECT_EFFECTS[connectorType]
  const isConnection = connectionName !== undefined

  const { data: affectedReposData, isLoading: isLoadingRepos, isError: isReposError } = useContextSources(
    projectId,
    undefined,
    { kind: ContextSourceKinds.Repository, connectorType, connectorId, createdByCurrentUser: true },
    { enabled: open && !aiProviderEffect },
  )

  const affectedRepos = affectedReposData?.items ?? []

  const title = isConnection ? `Disconnect ${connectionName}?` : `Disable ${label}?`
  const effect = aiProviderEffect
    ?? (isConnection
      ? `Disconnecting removes this ${label} connection and its stored token from this project. Repositories and wiki pages added through it are removed from the project context.`
      : 'Disabling this connector will remove access to all linked repositories in this project.')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-[640px] gap-6 p-8 shadow-[0_0_12px_rgba(0,36,50,0.16)]">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold tracking-[0.015em] text-card-foreground">{title}</DialogTitle>
          <DialogClose aria-label="Close" className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <p className="text-base font-normal leading-[150%] tracking-[0.015em] text-foreground" data-testid="disconnect-connector-effect">
          {effect}
        </p>

        {isLoadingRepos ? (
          <div className="text-sm text-muted-foreground" role="status" aria-live="polite">Loading...</div>
        ) : affectedRepos.length > 0 ? (
          <div
            className="bg-warning rounded-lg p-4 border border-warning-foreground"
            data-testid="affected-repos-warning"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-warning-foreground shrink-0 mt-0.5" />
              <div>
                <p className="text-base font-normal leading-[150%] tracking-[0.015em] text-warning-foreground">
                  The following repositories will be hidden from Chats:
                </p>
                <ul className="mt-5 ml-4 space-y-2 list-disc list-inside">
                  {affectedRepos.map((repo) => (
                    <li key={repo.id} className="text-base leading-[150%] tracking-[0.015em] text-warning-foreground" data-testid={`affected-repo-${repo.id}`}>
                      <span className="font-bold">{repo.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : isReposError ? (
          <p role="alert" className="text-sm text-destructive">Could not load the affected repositories.</p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDisconnecting || isLoadingRepos}
            data-testid={isConnection ? 'confirm-disconnect-connection' : 'disable-connector-button'}
          >
            <X className="h-4 w-4 mr-1" />
            {isConnection
              ? (isDisconnecting ? 'Disconnecting...' : 'Disconnect')
              : (isDisconnecting ? 'Disabling...' : 'Disable Connector')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
